<template>
  <!-- <b-overlay :show="isLoading" :opacity="0.7" blur="4px"> -->
    <div class="user-search">
      <v-select
        :key="vSelectKey"
        v-model="user"
        :reduce="(item) => item.value"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :options="users.filter(u => !u.isMe)"
        label="text"
        :placeholder="placeholder" debounce="200"
        :clearable="false"
        :multiple="multiple"
        autoscroll
        :loading="isLoading"
        @open="onOpen"
        @close="onClose"
        @search="keyword => GET_HAS_NEXT_USERS_PAGE ? searchUser(keyword) : ''"
        @mousewheel.native.stop
        @DOMMouseScroll.native.stop
      >
        <template v-if="!multiple" #selected-option="{ avatar, text, isOnline, tags, value }">
          <div class="d-flex align-items-center w-100 justify-content-between single">
            <div class="d-flex align-items-center">
              <b-avatar
                :src="getAvatar(avatar)"
                size="30"
                :badge="!!value"
                :badge-variant="isOnline ? 'success' : 'secondary'"
                :text="text?.charAt(0)" class="mr-1" variant="light-primary" />
              <span> {{ IS_MOBILE() ? FORMAT_NAME(text) : text }}</span>
            </div>
            <div class="d-flex align-items-center usersearch-tags">
              <tag-badge
                v-for="(t, idx) in (tags || []).slice(0, getTagCount)"
                :key="t.id || idx" 
                :name="t.name || t"
                :color="t.color || '#6c757d'"
                :description="t.description || ''"
                size="small"
              />
              <b-badge
                v-if="(tags || []).length > getTagCount"
                v-b-tooltip.hover="`${tags.length - getTagCount} more tag ${tags.length - getTagCount > 1 ? 's' : ''}`"
                variant="info"
                class="cursor-pointer"
                style="margin-left: -12px; margin-right: 16px;"
              >
                + {{tags.length - getTagCount}}
              </b-badge>
            </div>
          </div>
        </template>
        <template #option="{ avatar, text, isOnline, value, tags }">
          <div class="d-flex align-items-center w-100 justify-content-between">
            <div v-if="text" class="d-flex align-items-center">
              <b-avatar :src="getAvatar(avatar)" size="30" badge :badge-variant="isOnline ? 'success' : 'secondary'"
                :text="text.charAt(0)" class="mr-1" :style="{ color: isSelected(value) && 'black' }"
                :variant="isSelected(value) ? 'warning' : 'light-primary'" />
              <span> {{ IS_MOBILE() ? FORMAT_NAME(text) : text }}</span>
            </div>
            <div class="d-flex align-items-center usersearch-tags">
              <tag-badge
                v-for="(t, idx) in (tags || []).slice(0, getTagCount)" 
                :key="t.id || idx" 
                :name="t.name || t"
                :color="t.color || '#6c757d'" 
                :description="t.description || ''" 
                size="small"
              />
              <b-badge
                v-if="(tags || []).length > getTagCount"
                variant="info"
                style="margin-left: -12px; margin-right: 16px;"
              >
                + {{(tags || []).length - getTagCount}}
              </b-badge>
            </div>
          </div>
        </template>

        <template #list-footer>
          <li v-show="GET_HAS_NEXT_USERS_PAGE" ref="loading" class="loader text-center py-1">
            <b-spinner v-if="showLoader || isLoading" />
          </li>
        </template>
      </v-select>
    </div>
  <!-- </b-overlay> -->
</template>

<script>
import vSelect from 'vue-select'
import useApollo from '@/plugins/graphql/useApollo'
import { BAvatar, BOverlay, BSpinner, BBadge } from 'bootstrap-vue'
import { getUserData } from '@/auth/utils'
import TagBadge from '@/views/Settings/UserTags/Components/TagBadge.vue'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  components: {
    vSelect,
    BAvatar,
    BOverlay,
    BSpinner,
    TagBadge,
    BBadge,
  },
  props: {
    value: {
      type: [Object, Number, String, Array],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Select User',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [Object, String],
      default: () => null,
    },
    enableAllUsers: {
      type: Boolean,
      default: false,
    },
    forceAllUsers: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      self: getUserData(),
      isLoading: false,
      showLoader: false,
      users: [],
      perPage: 10,
      currentPage: 1,
      total: 0,
      searchTerm: '',
      observer: new IntersectionObserver(this.fetchMoreUsers),
      savedScrollTop: null,
      savedScrollParent: null,
      hasInitializedValue: false,
      vSelectKey: 0,
      vSelectedWidth: 0,
    }
  },
  computed: {
    ...mapGetters('project', ['GET_USERS', 'GET_HAS_NEXT_USERS_PAGE']),
    isMe() {
      return this.placeholder === 'Me'
    },
    user: {
      get() {
        return this.isLoading ? null : this.value
      },
      set(value) {
        // eslint-disable-next-line no-param-reassign
        if (Array.isArray(value) && !value.length) value = null
        if ((this.multiple || this.enableAllUsers) && value && value.length > 0) {
          if (value[value.length - 1] === null) {
            this.$emit('input', null)
            this.$forceUpdate()
          } else this.$emit('input', Array.isArray(value) ? value.filter(val => val !== null) : value)
          return
        }
        this.$emit('input', value)
      },
    },
    getTagCount() {
      return this.vSelectedWidth > 320 ? 2 : 1
    },
  },
  watch: {
    GET_USERS(val) {
      if (val && val.length) {
        val.forEach(user => {
          if (!this.users.some(existingUser => existingUser.value === user.value)) {
            this.users.push(user)
          }
        })
        
        // Add "All Users" option if enabled and not already present
        if ((this.enableAllUsers || this.multiple) && !this.users.find(u => u.value === null)) {
          this.users.unshift({ value: null, text: 'All Users' })
        }
        
        // Restore scroll position after users are added
        if (this.savedScrollTop !== null) {
          this.$nextTick(() => {
            if (this.savedScrollParent) {
              this.savedScrollParent.scrollTop = this.savedScrollTop
              this.savedScrollTop = null
              this.savedScrollParent = null
            }
          })
        }

        if (this.GET_USERS.length === this.total) {
          this.updateHasNextUsersPage(false)
        }
      }
    },
    enableAllUsers: {
      handler(val) {
        if (val && !this.users.find(u => u.value === null)) {
          this.users.unshift({ value: null, text: 'All Users' })
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
      },
      immediate: true,
    }
  },
  created() {
    if (this.GET_USERS && this.GET_USERS.length) {
      this.users = [...this.GET_USERS]
    } else {
      this.isLoading = true
      this.getUser()
    }
    // Add "All Users" option FIRST if enabled
    if ((this.enableAllUsers || this.multiple) && !this.users.find(u => u.value === null)) {
      this.users.unshift({ value: null, text: 'All Users' })
    }
    if (!this.value && this.isMe && !this.forceAllUsers) {
      this.$emit('input', getUserData().uuid)
    }
  },
  mounted() {
    this.vSelectedWidth = document.querySelector('.vs__selected-options')?.clientWidth
  },
  methods: {
    ...mapActions('project', ['updateUsers', 'updateHasNextUsersPage']),
    async onOpen() {
      if (this.GET_HAS_NEXT_USERS_PAGE) {
        await this.$nextTick()
        this.observer.observe(this.$refs.loading)
      }
    },
    onClose() {
      this.observer.disconnect()
    },
    searchUser(keyword) {
      this.searchTerm = keyword
      if (keyword) this.getUser(keyword)
    },
    async fetchMoreUsers([{ isIntersecting, target }]) {
      if (isIntersecting && !this.searchTerm && this.GET_HAS_NEXT_USERS_PAGE && !this.showLoader) {
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        
        // Save scroll position to be restored in watcher
        this.savedScrollTop = scrollTop
        this.savedScrollParent = ul
        
        this.showLoader = true
        this.currentPage += 1
        this.getUser('', this.currentPage, false, false)
      }
    },
    afterFetch(response) {
      const { data } = response.data.users
      const { isOnline, tags } = response.data.me
      
      // Add "All Users" option FIRST if enabled
      if ((this.enableAllUsers || this.multiple) && !this.users.find(u => u.value === null)) {
        this.users.unshift({ value: null, text: 'All Users' })
      }
      
      // Check if current user already exists
      const meExists = this.users.find(user => user.value === this.self.uuid)

      // Exclude current user and users that are already fetched from fetched data to avoid duplicates
      const users = data.filter(user => user.value !== this.self.uuid)
        .filter(user => !this.users.find(u => u.value === user.value))

      // Update isOnline status if current user already exists in users list
      if (meExists) {
        this.users.find(u => u.value === this.self.uuid).isOnline = isOnline
      } else if (!meExists) {
        // Add current user from stored data (after "All Users" if it exists)
        const { uuid: value, avatar } = this.self
        const meUser = {
          value,
          avatar,
          isOnline,
          text: 'Me',
          tags: tags ?? [],
        }
        // Insert "Me" after "All Users" if it exists
        if (this.enableAllUsers && this.users[0]?.value === null) {
          this.users.splice(1, 0, meUser)
        } else {
          this.users.unshift(meUser)
        }
      }

      // Append the rest of the fetched users
      this.users.push(...users)

      if (!this.searchTerm && !users.find(user => user.value === this.user)) {
        let userUuid = this.users?.[0]?.value
        
        if (this.multiple || this.enableAllUsers) userUuid = null
        if (!this.user || !this.value) this.user = userUuid
      }
      
      // Only set to "All Users" on initial load, not on every search
      if (this.enableAllUsers && !this.hasInitializedValue) {
        this.hasInitializedValue = true
        this.user = null
        this.vSelectKey += 1
      }
      // if (users.length === 1) {
      //   this.$nextTick(() => {
      //     if (!this.multiple && !this.value) this.$emit('input', users[0].value)
      //   })
      // } else {
      //   if (!users.find(user => user.value === this.user)) {
      //     let userUUid = this.users?.[0]?.value
      //     if (this.multiple) {
      //       userUUid = null
      //     }
      //     if (!this.user || !this.value) this.user = userUUid
      //   }
      // }

      this.updateUsers(this.users.filter(u => u.value !== null))
    },
    getUserByCompany(keyword) {
      useApollo.users.searchUserByCompany({ keyword, companyUid: this.companyId }).then(response => {
        this.afterFetch(response)
      }).catch(error => {
        this.showErrorMessage(error)
      })
    },
    getUser: _.debounce(function (keyword = '', page = 1, loading = false, showLoader = true) {
      if (this.companyId) {
        this.getUserByCompany(keyword)
        return
      }
      if (showLoader) this.isLoading = true
      useApollo.users.searchUser({
        keyword,
        projectId: this.$store.state.project.selectedProject,
        first: this.perPage,
        page,
      }).then(response => {
        if (!keyword) this.total = response.data.users.total
        this.afterFetch(response)
        if (!keyword) this.updateHasNextUsersPage(response.data.users.hasMorePages)
      }).finally(() => {
        if (typeof loading === 'function') loading(false)
        this.isLoading = false
        this.showLoader = false
      })
    }, 1000),
    isSelected(item) {
      return (this.value === item) || (Array.isArray(this.value) && this.value.includes(item))
    },
  },
}
</script>

<style lang="scss">
/* Ensure TagBadge triangular edge and hole render inside vue-select option/selected areas */
.user-search {
  .vs__dropdown-option .tag-badge:before,
  .vs__selected-options .tag-badge:before,
  .usersearch-tags .tag-badge:before {
    content: "";
    position: absolute;
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 12px solid var(--tag-bg);
    z-index: 0;
  }

  .vs__dropdown-option .tag-hole,
  .vs__selected-options .tag-hole,
  .usersearch-tags .tag-hole {
    position: absolute;
    top: 50%;
    left: 8px;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.32) inset;
    border: 2px solid rgba(0, 0, 0, 0.08);
    z-index: 2;
  }

  /* Remove default selected chip background inside v-select when rendering tag badges on the right */
  .vs--single .vs__selected {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 5px 10px 2px 10px !important;
    width: 94% !important;
    flex: 92%;
  }

  .tag-badge-wrap {
    margin-right: 16px !important;
  }

  .vs__search {
    padding: 0;
    flex: 1;
  }
}

.vs__deselect {
  svg {
    fill: #999;
  }
}
</style>
